import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import SplashScreen from './components/SplashScreen';
import LockdownScreen from './components/LockdownScreen';
import Home from './pages/Home';
import Category from './pages/Category';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminProducts from './pages/AdminProducts';
import AdminStock from './pages/AdminStock';
import AdminStockAdjust from './pages/AdminStockAdjust';
import AdminStockApprovals from './pages/AdminStockApprovals';
import AdminDayClose from './pages/AdminDayClose';
import AdminDiscrepancies from './pages/AdminDiscrepancies';
import AdminLabelPrint from './pages/AdminLabelPrint';
import AdminZones from './pages/AdminZones';
import MaintenanceGate from './components/MaintenanceGate';
import ErrorBoundary from './components/ErrorBoundary';
import ShortLinkRedirect from './pages/ShortLinkRedirect';
import AdminOrders from './pages/AdminOrders';
import AdminCategories from './pages/AdminCategories';
import AdminSettings from './pages/AdminSettings';
import AdminLockdown from './pages/AdminLockdown';
import AdminCutoffSettings from './pages/AdminCutoffSettings';
import AdminBroadcast from './pages/AdminBroadcast';
import EmailCampaigns from './pages/EmailCampaigns';
import AdminInquiries from './pages/AdminInquiries';
import AdminCustomers from './pages/AdminCustomers';
import AdminDrivers from './pages/AdminDrivers';
import AdminDispatch from './pages/AdminDispatch';
import AdminCredentials from './pages/AdminCredentials';
import AdminUsers from './pages/AdminUsers';
import AdminAttendance from './pages/AdminAttendance';
import AdminMyWork from './pages/AdminMyWork';
import AdminWorkTracking from './pages/AdminWorkTracking';
import AdminDeskTracker from './pages/AdminDeskTracker';
import AdminLayoutPage from './pages/AdminLayoutPage';
import AdminNotificationsLog from './pages/AdminNotificationsLog';
import AdminOutstanding from './pages/AdminOutstanding';
import AdminWarehouses from './pages/AdminWarehouses';
import WmsMarketing from './pages/WmsMarketing';
import AdminWmsContacts from './pages/AdminWmsContacts';
import AdminBranches from './pages/AdminBranches';
import AdminPallets from './pages/AdminPallets';
import AdminGoodsReceiving from './pages/AdminGoodsReceiving';
import AdminAllocations from './pages/AdminAllocations';
import AdminExportDashboard from './pages/AdminExportDashboard';
import AccessActivity from './pages/AccessActivity';
import AdminTabsShell from './components/AdminTabsShell';
import FloorTasks from './pages/FloorTasks';
import BranchLogin from './pages/BranchLogin';
import BranchMyWork from './pages/BranchMyWork';
import BranchDeskTracker from './pages/BranchDeskTracker';
import BranchDeskTrackerLogin from './pages/BranchDeskTrackerLogin';
import BranchNewOrder from './pages/BranchNewOrder';
import BranchOrders from './pages/BranchOrders';
import BranchPallets from './pages/BranchPallets';
import BranchReceiving from './pages/BranchReceiving';
import CustomerLogin from './pages/CustomerLogin';
import MyOrders from './pages/MyOrders';
import DriverLogin from './pages/DriverLogin';
import DriverSignup from './pages/DriverSignup';
import DriverDashboard from './pages/DriverDashboard';
import TrackOrder from './pages/TrackOrder';
import SiteMap from './pages/SiteMap';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import BottomNav from './components/BottomNav';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PwaManifestSwitcher from './components/PwaManifestSwitcher';
import StandaloneRoleRouter from './components/StandaloneRoleRouter';
import useActivityHeartbeat from './hooks/useActivityHeartbeat';
import { install401Interceptor } from './lib/axios401';

// Feb 2026 — Wire the global 401 interceptor once, at module load,
// BEFORE any component mounts an axios call. Any expired/invalid
// token anywhere in the app clears storage and bounces the user to
// the correct login screen. Login endpoints are exempt (see
// axios401.js) so wrong-password errors still render inline.
install401Interceptor();

// Renders nothing; simply mounts the activity heartbeat inside a
// <BrowserRouter> so `useLocation()` works. Lets us reuse the hook
// on the driver-host route tree without duplicating boilerplate.
const ActivityBeacon = () => { useActivityHeartbeat(); return null; };
import { Toaster } from './components/ui/toaster';
// Feb 2026 — DeskTracker + attendance screens fire `toast(...)` calls
// via the `sonner` package. The Radix `Toaster` above only renders
// events raised by `useToast()`, so sonner-driven success/error
// pills were invisible until this second root was mounted. Both
// systems coexist safely because each renders its own portal.
import { Toaster as SonnerToaster } from 'sonner';
import { CartProvider } from './context/CartContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { DriverAuthProvider } from './context/DriverAuthContext';
import { BranchAuthProvider } from './context/BranchAuthContext';
import { CategoriesProvider } from './context/CategoriesContext';
import { SettingsProvider, TypographyApplier } from './context/SettingsContext';
import { isDriverHost } from './lib/pwaHost';

/**
 * On the dedicated driver subdomain (e.g. driver.mrpco.net), the entire
 * origin is the Driver PWA — there is no customer storefront. Any
 * request to `/` (or any non-driver path) gets sent to /driver. If the
 * user already has a driver token, we land them directly on the
 * dashboard; otherwise on the login screen.
 */
const DriverHostRoot = () => {
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('mr_driver_token');
  return <Navigate to={hasToken ? '/driver' : '/driver/login'} replace />;
};

const StoreShell = ({ children }) => {
  const location = useLocation();
  useActivityHeartbeat();
  const path = location.pathname;
  const isAdmin = path.startsWith('/admin');
  const isDriver = path.startsWith('/driver');
  const isBranch = path.startsWith('/branch');
  const isTrack = path.startsWith('/track');
  const isWarehouse = path.startsWith('/warehouse');
  const isSitemap = path === '/admin/sitemap';
  const isPlain = isAdmin || isDriver || isBranch || isTrack || isWarehouse || isSitemap;

  // Splash: only on storefront routes, only once per browser session
  const [showSplash, setShowSplash] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (isPlain) return false;
    try {
      return sessionStorage.getItem('mrp_splash_shown') !== '1';
    } catch (e) {
      return true;
    }
  });

  useEffect(() => {
    if (!showSplash) return;
    try { sessionStorage.setItem('mrp_splash_shown', '1'); } catch (e) {}
  }, [showSplash]);

  return (
    <>
      <PwaManifestSwitcher />
      {showSplash && !isPlain && (
        <SplashScreen onDone={() => setShowSplash(false)} />
      )}
      {!isPlain && <Navbar />}
      {children}
      {!isPlain && <Footer />}
      {!isPlain && <CartDrawer />}
      {!isPlain && <BottomNav />}
      {!isPlain && <PWAInstallPrompt />}
    </>
  );
};

// Feb 2026 — the /admin/login route MUST stay reachable while lockdown
// is active. Otherwise a super admin whose session has expired (or is
// on a fresh browser) can't even paint the login form to sign back in
// and lift the lockdown. LockdownGate wraps the whole route tree, so
// we bypass it entirely on this one path.
const ADMIN_LOGIN_BYPASS_PATHS = ['/admin/login'];
function isAdminLoginPath() {
  if (typeof window === 'undefined') return false;
  const p = (window.location.pathname || '').replace(/\/+$/, '') || '/';
  return ADMIN_LOGIN_BYPASS_PATHS.includes(p);
}

function App() {
  if (isAdminLoginPath()) {
    return <AppRouter />;
  }
  return (
    <LockdownGate>
      <AppRouter />
    </LockdownGate>
  );
}

// Feb 2026 v7 — LockdownGate polls the public /api/lockdown/status
// endpoint. When enabled, it fires a probe request to /api/admin/session
// with the stored admin token: a 200 response ⇒ super admin (only they
// bypass the lockdown middleware) ⇒ render the normal app. Anything
// else ⇒ render the beautiful LockdownScreen with the configured
// title/message/contact.
const LockdownGate = ({ children }) => {
  const [status, setStatus] = useState(null); // null → loading first status
  const [bypass, setBypass] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.get(`${backendUrl}/api/lockdown/status`, { timeout: 6000 });
        if (cancelled) return;
        setStatus(data || { enabled: false });
        if (data && data.enabled) {
          // Probe: is the current token a super admin? Only they get
          // 200 from /api/admin/me while lockdown is active (the
          // middleware 503s every other authenticated call).
          const token = localStorage.getItem('mr_admin_token');
          if (token) {
            try {
              await axios.get(`${backendUrl}/api/admin/me`, {
                headers: { Authorization: `Bearer ${token}` }, timeout: 6000,
              });
              if (!cancelled) setBypass(true);
            } catch (_err) {
              if (!cancelled) setBypass(false);
            }
          } else {
            setBypass(false);
          }
        } else {
          setBypass(false);
        }
      } catch (_err) {
        if (!cancelled) setStatus({ enabled: false });
      }
    };
    check();
    const t = setInterval(check, 30000);  // re-poll every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, [backendUrl]);

  // Never flash the lockdown screen before we know the state.
  if (status === null) return children;
  if (status.enabled && !bypass) {
    return (
      <LockdownScreen
        title={status.title}
        message={status.message}
        contact={status.contact}
      />
    );
  }
  return children;
};

function AppRouter() {
  // On the dedicated driver subdomain we serve a minimal route tree —
  // no storefront pages, no Navbar, no CartDrawer, no splash. This
  // makes the driver bundle behave like a fully separate app even
  // though it's the same React build.
  if (isDriverHost()) {
    return (
      <div className="App">
        <DriverAuthProvider>
          <SettingsProvider>
            <BrowserRouter>
              <TypographyApplier />
              <PwaManifestSwitcher />
              <ActivityBeacon />
              <Routes>
                <Route path="/driver/login" element={<DriverLogin />} />
                <Route path="/driver/signup" element={<DriverSignup />} />
                <Route path="/driver" element={<DriverDashboard />} />
                {/* Any other path on this origin → driver entry point */}
                <Route path="*" element={<DriverHostRoot />} />
              </Routes>
              <PWAInstallPrompt />
              <Toaster />
              <SonnerToaster position="top-right" richColors closeButton />
            </BrowserRouter>
          </SettingsProvider>
        </DriverAuthProvider>
      </div>
    );
  }

  return (
    <div className="App">
      <CustomerAuthProvider>
        <AdminAuthProvider>
          <DriverAuthProvider>
            <BranchAuthProvider>
            <CategoriesProvider>
              <SettingsProvider>
                <CartProvider>
                  <BrowserRouter>
                    <TypographyApplier />
                    <StandaloneRoleRouter />
                    <MaintenanceGate>
                    <StoreShell>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/wms" element={<WmsMarketing />} />
                        <Route path="/admin/wms-contacts" element={<AdminWmsContacts />} />
                        <Route path="/s/:slug" element={<ShortLinkRedirect />} />
                        <Route path="/category/:slug" element={<Category />} />
                        <Route path="/login" element={<CustomerLogin />} />
                        <Route path="/my-orders" element={<MyOrders />} />
                        <Route path="/track/:orderNumber" element={<TrackOrder />} />
                        <Route path="/driver/login" element={<DriverLogin />} />
                        <Route path="/driver/signup" element={<DriverSignup />} />
                        <Route path="/driver" element={<DriverDashboard />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        {/* Feb 2026 — Every other /admin/* path funnels
                            through the state-preserving tab shell so
                            an admin can keep Orders + Stock Adjust +
                            Outstanding open simultaneously without
                            losing filters or scroll position. The
                            authoritative route→component map lives in
                            `src/config/adminRoutes.js`. */}
                        <Route
                          path="/admin/*"
                          element={(
                            <ErrorBoundary label="admin-shell">
                              <AdminTabsShell />
                            </ErrorBoundary>
                          )}
                        />
                        <Route path="/warehouse" element={<FloorTasks />} />
                        <Route path="/warehouse/tasks" element={<FloorTasks />} />
                        <Route path="/warehouse/floor" element={<FloorTasks />} />
                        <Route path="/warehouse/login" element={<AdminLogin />} />
                        <Route path="/branch/login" element={<BranchLogin />} />
                        <Route path="/branch" element={<BranchNewOrder />} />
                        <Route path="/branch/orders" element={<BranchOrders />} />
                        <Route path="/branch/pallets" element={<BranchPallets />} />
                        <Route path="/branch/receiving" element={<BranchReceiving />} />
                        <Route path="/branch/my-work" element={<BranchMyWork />} />
                        <Route path="/branch/desk-tracker/login" element={<BranchDeskTrackerLogin />} />
                        <Route path="/branch/desk-tracker" element={<BranchDeskTracker />} />
                      </Routes>
                    </StoreShell>
                    </MaintenanceGate>
                    <Toaster />
                    <SonnerToaster position="top-right" richColors closeButton />
                  </BrowserRouter>
                </CartProvider>
              </SettingsProvider>
            </CategoriesProvider>
            </BranchAuthProvider>
          </DriverAuthProvider>
        </AdminAuthProvider>
      </CustomerAuthProvider>
    </div>
  );
}

export default App;
